/**
=========================================================
* Material Dashboard 2 React - v2.0.0
=========================================================

* Skeleton by: www.creative-tim.com

Heavly modified by www.stefanofrasca.com | @fefoweb

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from 'react';

// react-router components
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { SnackbarProvider } from 'notistack';

// @mui material components
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Material Dashboard 2 React components
// import MDBox from 'components/MDBox';

// Material Dashboard 2 React example components
import Sidenav from 'examples/Sidenav';
import Configurator from 'examples/Configurator';

// Material Dashboard 2 React themes
import theme from 'assets/theme';

// Material Dashboard 2 React Dark Mode themes
import themeDark from 'assets/theme-dark';

// RTL plugins
import rtlPlugin from 'stylis-plugin-rtl';
import createCache from '@emotion/cache';

// Material Dashboard 2 React routes
import routes from 'routes';

// Material Dashboard 2 React contexts + setOpenConfigurator
import { useMaterialUIController, setMiniSidenav } from 'context';
import useNetworkStatus from 'hook/use-network-status';
import { Alert, Snackbar } from '@mui/material';
import { isStorageAvailable } from 'lib/storage';

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const hasStorageAvailable = isStorageAvailable();
  // +openConfigurator
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { isOnline, isInternetAvailable } = useNetworkStatus(true);

  const [open, setOpen] = useState(false);
  const [openNoStorage, setOpenNoStorage] = useState(false);
  const [, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: 'ltr',
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseNoStorage = () => {
    setOpenNoStorage(false);
  };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Effetto per gestire il side effect (impostare open)
  useEffect(() => {
    if (!isOnline || !isInternetAvailable) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isOnline, isInternetAvailable]);

  // Effetto per gestire il side effect (impostare open)
  useEffect(() => {
    if (!hasStorageAvailable) {
      setOpenNoStorage(true);
    } else {
      setOpenNoStorage(false);
    }
  }, [hasStorageAvailable]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = null;

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <SnackbarProvider maxSnack={3} preventDuplicate>
        <CssBaseline />
        {layout === 'dashboard' && (
          <>
            <Sidenav
              color={sidenavColor}
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === 'vr' && <Configurator />}
        {(!isOnline || !isInternetAvailable) && (
          <Snackbar
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="warning" variant="filled" sx={{ width: '100%' }}>
              !!🚨 Attenzione!! Il tuo dispositivo sembrerebbe{' '}
              <strong>NON essere connesso a Internet</strong>.<br />
              Per poter utilizzare correttamente l&#39;applicativo{' '}
              <strong>devi avere una connessione attiva</strong>.
            </Alert>
          </Snackbar>
        )}
        {!hasStorageAvailable && (
          <Snackbar
            open={openNoStorage}
            onClose={handleCloseNoStorage}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="warning" variant="filled" sx={{ width: '100%' }}>
              !!🚨 Attenzione!! Per funzionare correttamente, questa applicazione richiede
              l&#39;accesso all&#39;archiviazione locale (<strong>localStorage</strong>) del
              browser.
            </Alert>
          </Snackbar>
        )}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
